import { default as React, useEffect, useState } from 'react';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import {
  DevHubBox,
  DevHubButton,
  DevHubDynamicIcon,
  DevHubGrid,
  DevHubIconButton,
  DevHubInputAdornment,
  DevHubLink,
  DevHubTextField,
} from '@devxdiscover/devhub-ui';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useApi } from '@backstage/core-plugin-api';
import { Component } from './types/InsightsTypes';
import { Progress } from '@backstage/core-components';
import { getEntityComponents } from './FetchEntityComponents';

/**
 * Entry point into the documentation tab
 */
export const ApplicationDocs = () => {
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const [loading, setLoading] = React.useState(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filteredDocs, setFilteredDocs] = React.useState<Component[]>([]);

  const filterDocs = (query: string) => {
    return filteredDocs.filter(doc =>
      doc.name.toLowerCase().includes(query.toLowerCase()),
    );
  };

  useEffect(() => {
    if (localStorage.getItem('theme') === 'devhub-dark') {
      import('primereact/resources/themes/md-dark-indigo/theme.css');
    } else {
      import('primereact/resources/themes/md-light-indigo/theme.css');
    }
  }, []);

  useEffect(() => {
    const fetchDocs = async () => {
      const { entityComponents } = await getEntityComponents(
        entity,
        catalogApi,
        'include',
        ['docs', 'website', 'service'],
      );
      setFilteredDocs(entityComponents);
      setLoading(false);
    };
    fetchDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity]);

  const handleSearch = () => {
    setFilteredDocs(filterDocs(searchQuery));
  };

  const clearSearch = () => {
    setSearchQuery('');
    setFilteredDocs(filterDocs(''));
  };

  const nameLink = (doc: Component) => {
    const componentId = doc.id;
    return (
      <DevHubLink href={`/catalog/default/component/${componentId}/docs`}>
        {doc.name}
      </DevHubLink>
    );
  };

  const ownerLink = (doc: Component) => {
    const [group, namespace, name] = doc.owner.split(/[:/]/);
    return (
      <DevHubLink href={`catalog/${namespace}/${group}/${name}`}>
        {doc.owner}
      </DevHubLink>
    );
  };

  const searchBarHeader = (
    <DevHubBox display="flex" paddingBottom={0.25}>
      <DevHubGrid container alignItems="center" justifyContent="flex-end">
        <DevHubGrid item sm={12} md={3}>
          <DevHubTextField
            id="docs-search"
            label="Search"
            margin="normal"
            variant="outlined"
            value={searchQuery}
            disabled={loading}
            noMargin
            placeholder="Enter your search text"
            onChange={(e: {
              target: { value: React.SetStateAction<string> };
            }) => setSearchQuery(e.target.value)}
            onKeyDown={(e: { key: string }) =>
              e.key === 'Enter' && handleSearch()
            }
            size="small"
            InputProps={
              searchQuery
                ? {
                    endAdornment: (
                      <DevHubInputAdornment position="end">
                        <DevHubIconButton onClick={clearSearch}>
                          <DevHubDynamicIcon iconName="Clear" />
                        </DevHubIconButton>
                      </DevHubInputAdornment>
                    ),
                  }
                : {}
            }
          />
        </DevHubGrid>
        <DevHubGrid item sm={12} md={1}>
          <DevHubButton
            variant="contained"
            buttonColor="black"
            size="large"
            height="large"
            href="#"
            disabled={loading}
            onClick={handleSearch}
          >
            <DevHubDynamicIcon iconName="Search" />
          </DevHubButton>
        </DevHubGrid>
      </DevHubGrid>
    </DevHubBox>
  );
  return (
    <DevHubBox>
      {loading ? (
        <Progress />
      ) : (
        <DataTable
          value={filteredDocs}
          paginator
          rows={25}
          emptyMessage="No documentation available"
          rowsPerPageOptions={[25, 50, 75, 100]}
          header={searchBarHeader}
          style={{ backgroundColor: 'white' }}
        >
          <Column
            key="name"
            field="name"
            header="Name"
            body={rowData => <span>{nameLink(rowData)}</span>}
            sortable
            headerStyle={{ backgroundColor: 'white' }}
          />
          <Column
            key="type"
            field="type"
            header="Type"
            body={rowData => <span>{rowData.type}</span>}
            sortable
            headerStyle={{ backgroundColor: 'white' }}
          />
          <Column
            key="owner"
            field="owner"
            header="Owner"
            body={rowData => <span>{ownerLink(rowData)}</span>}
            sortable
            headerStyle={{ backgroundColor: 'white' }}
          />
          <Column
            key="lifecycle"
            field="lifecycle"
            header="Lifecycle"
            body={rowData => <span>{rowData.lifecycle}</span>}
            sortable
            headerStyle={{ backgroundColor: 'white' }}
          />
          <Column
            key="description"
            field="description"
            header="Description"
            body={rowData => <span>{rowData.description}</span>}
            sortable
            headerStyle={{ backgroundColor: 'white' }}
          />
        </DataTable>
      )}
    </DevHubBox>
  );
};
